import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  Injector,
  signal,
} from '@angular/core';
import { handleRequestError } from '@softline/application';
import { ConnectionError, NotFoundError, ServerError } from '@softline/core';
import {
  DEFAULT_BELEGLESER_CONFIG,
  SOFTLINE_CONFIG_BELEGLESER,
} from '../belegleser.shared';
import { JobRepository } from '../repositories/job.repository';
import {
  Job,
  JobCreated,
  OverlayWord,
  PageGeometry,
  PageImageSource,
} from '../types/job';
import { ResultData } from '../types/result';
import { FinalResultReport } from '../types/service';

export type JobPhase =
  | 'idle'
  | 'uploading'
  | 'reading'
  | 'ready'
  | 'failed'
  | 'not-found'
  | 'unreachable'
  | 'timeout';

/**
 * Upload and poll state of ONE job. The service has no push channel, so `GET /jobs/{id}`
 * is polled until the job completes or fails. Route-scoped: the poll survives the
 * upload → review navigation.
 */
@Injectable()
export class JobService {
  private readonly repository = inject(JobRepository);
  private readonly config = inject(SOFTLINE_CONFIG_BELEGLESER);
  private readonly injector = inject(Injector);

  /** Bumped on every open/upload/reset/destroy; a stale poll loop sees the mismatch and stops. */
  private generation = 0;

  private readonly _phase = signal<JobPhase>('idle');
  private readonly _job = signal<Job | null>(null);
  private readonly _progress = signal(0);
  private readonly _error = signal<string | null>(null);
  private readonly _pages = signal<PageGeometry[]>([]);
  private readonly _dpi = signal(144);
  private readonly _words = signal<OverlayWord[]>([]);
  private readonly _startedAt = signal<number | null>(null);

  readonly phase = this._phase.asReadonly();
  readonly job = this._job.asReadonly();
  readonly jobId = computed(() => this._job()?.id ?? null);
  /** Upload progress 0..1. */
  readonly progress = this._progress.asReadonly();
  /** Reason of a `failed` job, as the service phrased it. */
  readonly error = this._error.asReadonly();
  readonly pages = this._pages.asReadonly();
  readonly dpi = this._dpi.asReadonly();
  readonly words = this._words.asReadonly();
  readonly startedAt = this._startedAt.asReadonly();
  readonly result = computed<ResultData | null>(() =>
    this._phase() === 'ready' ? (this._job()?.result ?? null) : null
  );
  readonly readByTemplate = computed(
    () => this._job()?.metadata?.mechanism === 'template'
  );
  /** Bound to the current job, so a new job hands out a new source and every page reloads. */
  readonly pageImageSource = computed<PageImageSource | null>(() => {
    const jobId = this.jobId();
    if (!jobId) return null;
    return (page) => this.repository.pageImage(jobId, page.page, page.rotation);
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.generation++);
  }

  async upload(file: File): Promise<string | null> {
    const generation = this.reset();
    this._phase.set('uploading');
    this._startedAt.set(Date.now());
    try {
      const created = await this.uploadFile(file);
      if (generation !== this.generation) return null;
      this._job.set({
        id: created.id,
        status: created.status,
        original_name: file.name,
        created_at: new Date().toISOString(),
        completed_at: null,
      });
      this._phase.set('reading');
      void this.loadPages(created.id, generation);
      void this.poll(created.id, generation);
      return created.id;
    } catch (e) {
      if (generation === this.generation) this.fail(e);
      return null;
    }
  }

  async open(jobId: string): Promise<void> {
    if (this._job()?.id === jobId && this._phase() !== 'idle') return;
    const generation = this.reset();
    this._phase.set('reading');
    try {
      const job = await this.repository.get(jobId);
      if (generation !== this.generation) return;
      this._job.set(job);
      await this.follow(job, generation);
    } catch (e) {
      if (generation === this.generation) this.fail(e);
    }
  }

  /** After `unreachable` or `timeout`: picks the same job up again. */
  async retry(): Promise<void> {
    const jobId = this._job()?.id;
    if (!jobId) return;
    this._phase.set('idle');
    await this.open(jobId);
  }

  async discard(): Promise<void> {
    const jobId = this._job()?.id;
    this.reset();
    if (!jobId) return;
    try {
      await this.repository.delete(jobId);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  /** Reports the result as forwarded to the target system; a second report replaces the first. */
  async reportFinalResult(result: ResultData): Promise<FinalResultReport | null> {
    const jobId = this._job()?.id;
    if (!jobId) return null;
    try {
      return await this.repository.reportFinalResult(jobId, result);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return null;
    }
  }


  private async follow(job: Job, generation: number): Promise<void> {
    if (job.status === 'completed') return this.complete(job, generation);
    if (job.status === 'failed') return this.failed(job);
    this._startedAt.set(Date.now());
    void this.loadPages(job.id, generation);
    void this.poll(job.id, generation);
  }

  private async poll(jobId: string, generation: number): Promise<void> {
    const interval =
      this.config.pollIntervalMs ?? DEFAULT_BELEGLESER_CONFIG.pollIntervalMs;
    const timeout = this.config.pollTimeoutMs ?? DEFAULT_BELEGLESER_CONFIG.pollTimeoutMs;
    const startedAt = Date.now();
    while (generation === this.generation) {
      await new Promise((resolve) => setTimeout(resolve, interval));
      if (generation !== this.generation) return;
      let job: Job;
      try {
        job = await this.repository.get(jobId);
      } catch (e) {
        if (generation === this.generation) this.fail(e);
        return;
      }
      if (generation !== this.generation) return;
      this._job.set(job);
      if (job.status === 'completed') return this.complete(job, generation);
      if (job.status === 'failed') return this.failed(job);
      if (Date.now() - startedAt >= timeout) {
        this._phase.set('timeout');
        return;
      }
    }
  }

  private async complete(job: Job, generation: number): Promise<void> {
    try {
      // Words are a display aid (snapping, word preview); without them the review still works.
      const [pages, words] = await Promise.all([
        this.repository.pages(job.id),
        this.repository.words(job.id).catch(() => ({ words: [] })),
      ]);
      if (generation !== this.generation) return;
      this._pages.set(pages.pages);
      this._dpi.set(pages.dpi);
      this._words.set(words.words);
      this._phase.set('ready');
    } catch (e) {
      if (generation === this.generation) this.fail(e);
    }
  }

  private failed(job: Job): void {
    this._error.set(job.error ?? null);
    this._phase.set('failed');
  }

  private fail(e: unknown): void {
    if (e instanceof NotFoundError) {
      this._phase.set('not-found');
      return;
    }
    if (e instanceof ConnectionError || e instanceof ServerError) {
      this._phase.set('unreachable');
      return;
    }
    this._phase.set('failed');
    handleRequestError(e, this.injector, { show: true });
  }

  private async loadPages(jobId: string, generation: number): Promise<void> {
    try {
      const pages = await this.repository.pages(jobId);
      if (generation !== this.generation) return;
      this._pages.set(pages.pages);
      this._dpi.set(pages.dpi);
    } catch {
      // The pages answer 409 until the PDF is stored; completion loads them again.
    }
  }

  private uploadFile(file: File): Promise<JobCreated> {
    return new Promise<JobCreated>((resolve, reject) => {
      this.repository.upload(file).subscribe({
        next: (event) => {
          if (event.type === 'progress' && event.total)
            this._progress.set(event.loaded / event.total);
          else if (event.type === 'response' && event.response) {
            this._progress.set(1);
            resolve(event.response);
          }
        },
        error: reject,
        complete: () => reject(new Error('upload finished without a job id')),
      });
    });
  }

  private reset(): number {
    this.generation++;
    this._phase.set('idle');
    this._job.set(null);
    this._progress.set(0);
    this._error.set(null);
    this._pages.set([]);
    this._dpi.set(144);
    this._words.set([]);
    this._startedAt.set(null);
    return this.generation;
  }
}
